import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';

// ============================================================================
// Otto-Lister — Feld-Override je Variante (im reduzierten Kind-Lister).
//
// Zeigt die Felder des geteilten Kategorie-Mapping-Profils (Vater). Default je Feld = geerbte
// Quelle „vom Hauptartikel". Pro Feld kann der Händler „für diese Variante abweichen" und eine
// eigene Quelle setzen (Fester Wert / Vario-Feld / Vorlage). Gespeichert wird der Override am Kind
// (otto.listings.mapping_override via otto_save_listing_mapping_override). Vario-Feld-Mappings
// lösen ohnehin je Variante eigene Werte auf — Override ist für gezielte Ausnahmen.
// ============================================================================

export interface FieldOverridesCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  registerSaver?: (id: string, fn: () => Promise<void>) => void;
}
export interface FieldOverridesArgs {
  childListingId: string;
  ottoCategory: string | null;
  /** Vario-Artikel des Kindes — für die Vario-Feld-Auswahlliste (vario-mapping-sources). */
  varioArticleId: string;
  currentOverride: Record<string, OverrideEntry> | null;
  /** Wenn gesetzt: nur diese Otto-Feld-Keys anzeigen (fehlende werden als „nicht gemappt" ergänzt).
   *  Übrige Override-Keys bleiben beim Speichern erhalten. */
  restrictKeys?: string[];
  /** Optionale Überschrift (Default: „Felder · Variante vs. Hauptartikel"). */
  title?: string;
}

interface OverrideEntry { source_type: 'fixed_value' | 'vario_path' | 'template' | 'unmapped'; vario_path?: string | null; fixed_value?: string | null; template?: string | null }
interface ProfileField { otto_field_key: string; field_group: string; source_type: string; vario_path: string | null; fixed_value: unknown; template: string | null }
interface SourceDef { sourceKey: string; path: string; label: string }
interface SourceGroup { key: string; label: string; sources: SourceDef[] }

const SOURCE_LABELS: Record<string, string> = { fixed_value: 'Fester Wert', vario_path: 'Vario-Feld', template: 'Vorlage', unmapped: '— nicht gemappt —' };
const FIELD_LABELS: Record<string, string> = {
  ean: 'EAN', price: 'Otto-Preis', sku: 'SKU', productTitle: 'Titel', productDescription: 'Beschreibung',
  mpn: 'MPN (Hersteller-Nr.)', packagingDimensions: 'Verpackungsmaße',
  bulletPoint1: 'Stichpunkt 1', bulletPoint2: 'Stichpunkt 2', bulletPoint3: 'Stichpunkt 3', bulletPoint4: 'Stichpunkt 4', bulletPoint5: 'Stichpunkt 5',
};

export async function renderVariantFieldOverrides(host: HTMLElement, ctx: FieldOverridesCtx, args: FieldOverridesArgs): Promise<void> {
  if (!args.ottoCategory) {
    host.innerHTML = banner('warn', 'Keine OTTO-Kategorie gesetzt — Feld-Mapping ist kategoriegebunden (im Hauptartikel wählen).');
    return;
  }
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Felder…</div>`;

  let fields: ProfileField[];
  let varioOptions: Array<{ path: string; label: string }> = [];
  try {
    const [profileRes, sourcesRes] = await Promise.all([
      ctx.supabase.rpc('otto_get_mapping_profile', { _otto_category: args.ottoCategory }),
      // Vario-Feld-Auswahlliste (gleiche Quelle wie der Vater-Mapping-Editor).
      fetchSources(ctx, args.varioArticleId).catch(() => [] as Array<{ path: string; label: string }>),
    ]);
    if (profileRes.error) throw new Error(profileRes.error.message);
    fields = (((profileRes.data as { fields?: ProfileField[] } | null)?.fields) ?? []) as ProfileField[];
    varioOptions = sourcesRes;
  } catch (e) {
    host.innerHTML = banner('error', `Profil nicht ladbar: ${(e as Error).message}`);
    return;
  }

  // restrictKeys: nur diese Felder; fehlende als „nicht gemappt"-Zeile synthetisieren.
  if (args.restrictKeys && args.restrictKeys.length > 0) {
    const byKey = new Map(fields.map((f) => [f.otto_field_key, f]));
    fields = args.restrictKeys.map((k) => byKey.get(k) ?? { otto_field_key: k, field_group: 'variants', source_type: 'unmapped', vario_path: null, fixed_value: null, template: null });
  }
  if (fields.length === 0) {
    host.innerHTML = banner('info', 'Das Kategorie-Profil hat noch keine Felder — im Hauptartikel das Mapping pflegen. Danach kannst du hier je Variante abweichen.');
    return;
  }

  const override = { ...(args.currentOverride ?? {}) } as Record<string, OverrideEntry>;

  host.innerHTML = `
    <div class="v-pa-md">
      <div class="v-row v-row-sm" style="justify-content:space-between;align-items:center;margin-bottom:6px;">
        <h4 class="klst-detail-section" style="margin:0;">${escapeHtml(args.title ?? 'Felder · Variante vs. Hauptartikel')}</h4>
        <button type="button" class="v-button v-button--primary" id="otto-fo-save"><span class="v-icon fa-regular fa-floppy-disk"></span> Abweichungen speichern</button>
      </div>
      <p class="v-text-muted" style="font-size:12px;margin:0 0 8px;">
        Standard = geerbt vom Hauptartikel. „Vario-Feld"-Felder liefern je Variante automatisch eigene Werte.
        Setze „abweichend", um ein Feld nur für diese Variante zu überschreiben.
      </p>
      <div style="overflow:auto;">
        <table class="klst-detail-table" style="width:100%;border-collapse:collapse;">
          <thead><tr>
            <th style="text-align:left;">Otto-Feld</th>
            <th style="text-align:left;">Geerbt (Hauptartikel)</th>
            <th style="text-align:left;">Abweichend</th>
            <th style="text-align:left;">Quelle (nur bei Abweichung)</th>
          </tr></thead>
          <tbody>${fields.map((f) => fieldRow(f, override[f.otto_field_key], varioOptions)).join('')}</tbody>
        </table>
      </div>
    </div>`;

  // Toggle: Quelle-Editor je Zeile ein-/ausblenden.
  host.querySelectorAll<HTMLInputElement>('input[data-fo-toggle]').forEach((cb) => {
    const key = cb.dataset['foToggle']!;
    const editor = host.querySelector<HTMLElement>(`[data-fo-editor="${cssEscape(key)}"]`);
    const sync = () => { if (editor) editor.style.display = cb.checked ? '' : 'none'; };
    cb.addEventListener('change', sync); sync();
  });
  // Quelle-Typ-Wechsel: Vario-Feld → Auswahlliste; Freifeld/Formel → Textfeld; nicht gemappt → nichts.
  host.querySelectorAll<HTMLSelectElement>('select[data-fo-type]').forEach((sel) => {
    const key = sel.dataset['foType']!;
    const input = host.querySelector<HTMLInputElement>(`input[data-fo-value="${cssEscape(key)}"]`);
    const varioSel = host.querySelector<HTMLSelectElement>(`select[data-fo-vario="${cssEscape(key)}"]`);
    const sync = () => {
      const t = sel.value;
      if (varioSel) varioSel.style.display = t === 'vario_path' ? '' : 'none';
      if (input) {
        input.style.display = (t === 'fixed_value' || t === 'template') ? '' : 'none';
        input.placeholder = t === 'template' ? 'z. B. {article.manufacturer} {article.name}' : 'Fester Wert';
      }
    };
    sel.addEventListener('change', sync); sync();
  });

  // Keys, die in diesem Editor gerendert werden — übrige Override-Keys bleiben beim Speichern erhalten.
  const renderedKeys = new Set(fields.map((f) => f.otto_field_key));

  // Override-Save (liest DOM erst zur Ausführungszeit). Wirft bei Fehler — der eigene Button fängt
  // das ab (eigene Toasts), der globale saveAll-Handler meldet aggregiert.
  const persist = async (): Promise<void> => {
    // Übrige (nicht gerenderte) Overrides erhalten.
    const next: Record<string, OverrideEntry> = {};
    for (const [k, v] of Object.entries(override)) if (!renderedKeys.has(k)) next[k] = v;
    host.querySelectorAll<HTMLTableRowElement>('tr[data-fo-row]').forEach((tr) => {
      const key = tr.dataset['foRow']!;
      const cb = tr.querySelector<HTMLInputElement>('input[data-fo-toggle]');
      if (!cb?.checked) return;
      const type = tr.querySelector<HTMLSelectElement>('select[data-fo-type]')?.value as OverrideEntry['source_type'];
      const txt = tr.querySelector<HTMLInputElement>('input[data-fo-value]')?.value.trim() ?? '';
      const varioPath = tr.querySelector<HTMLSelectElement>('select[data-fo-vario]')?.value ?? '';
      const entry: OverrideEntry = { source_type: type };
      if (type === 'vario_path') entry.vario_path = varioPath;
      else if (type === 'template') entry.template = txt;
      else if (type === 'fixed_value') entry.fixed_value = txt;
      // 'unmapped' → kein Wert
      next[key] = entry;
    });
    const { error } = await ctx.supabase.rpc('otto_save_listing_mapping_override', { _id: args.childListingId, _override: next });
    if (error) throw new Error(error.message);
  };
  ctx.registerSaver?.('field-overrides', persist);

  host.querySelector<HTMLButtonElement>('#otto-fo-save')?.addEventListener('click', async () => {
    const btn = host.querySelector<HTMLButtonElement>('#otto-fo-save')!;
    btn.disabled = true;
    try {
      await persist();
      showToast('Feld-Abweichungen gespeichert.', 'success');
    } catch (e) {
      showToast(`Speichern fehlgeschlagen: ${(e as Error).message}`, 'error');
    } finally {
      btn.disabled = false;
    }
  });
}

function fieldRow(f: ProfileField, ov: OverrideEntry | undefined, varioOptions: Array<{ path: string; label: string }>): string {
  const key = f.otto_field_key;
  const inheritedSrc = SOURCE_LABELS[f.source_type] ?? f.source_type;
  const inheritedVal = f.source_type === 'vario_path' ? (f.vario_path ?? '') : f.source_type === 'template' ? (f.template ?? '') : (f.fixed_value != null ? String(f.fixed_value) : '');
  const on = ov != null;
  const ovType = ov?.source_type ?? 'vario_path';
  const ovText = ov ? (ov.source_type === 'template' ? (ov.template ?? '') : ov.source_type === 'fixed_value' ? (ov.fixed_value ?? '') : '') : '';
  const ovVarioPath = ov?.source_type === 'vario_path' ? (ov.vario_path ?? '') : '';
  const opt = (v: string, label: string) => `<option value="${v}"${ovType === v ? ' selected' : ''}>${label}</option>`;
  const varioOpts = `<option value="">— Vario-Feld wählen —</option>` + varioOptions
    .map((s) => `<option value="${escapeAttr(s.path)}"${s.path === ovVarioPath ? ' selected' : ''}>${escapeHtml(s.label)}</option>`).join('');
  return `
    <tr data-fo-row="${escapeAttr(key)}" style="border-bottom:1px solid var(--go-border,#e5e7eb);">
      <td style="padding:6px 8px;"><strong>${escapeHtml(FIELD_LABELS[key] ?? key)}</strong><div class="v-text-muted" style="font-size:11px;">${escapeHtml(key)}</div></td>
      <td style="padding:6px 8px;"><span class="v-text-muted" style="font-size:12px;">${escapeHtml(inheritedSrc)}${inheritedVal ? `: ${escapeHtml(inheritedVal)}` : ''}</span></td>
      <td style="padding:6px 8px;"><label style="display:flex;align-items:center;gap:6px;"><input type="checkbox" data-fo-toggle="${escapeAttr(key)}"${on ? ' checked' : ''}/> abweichend</label></td>
      <td style="padding:6px 8px;min-width:280px;">
        <div data-fo-editor="${escapeAttr(key)}" style="display:none;" class="v-row v-row-sm">
          <select class="v-input" data-fo-type="${escapeAttr(key)}" style="width:140px;">
            ${opt('vario_path', 'Vario-Feld')}${opt('fixed_value', 'Freifeld')}${opt('template', 'Formel')}${opt('unmapped', 'Nicht gemappt')}
          </select>
          <select class="v-input" data-fo-vario="${escapeAttr(key)}" style="flex:1;min-width:160px;">${varioOpts}</select>
          <input class="v-input" data-fo-value="${escapeAttr(key)}" type="text" value="${escapeAttr(ovText)}" style="flex:1;min-width:120px;" />
        </div>
      </td>
    </tr>`;
}

/** Vario-Feld-Auswahlliste (gleiche Quelle wie der Vater-Mapping-Editor: vario-mapping-sources). */
async function fetchSources(ctx: FieldOverridesCtx, varioArticleId: string): Promise<Array<{ path: string; label: string }>> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/vario-mapping-sources`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'list', articleId: varioArticleId }),
  });
  if (!res.ok) throw new Error(`vario-mapping-sources ${res.status}`);
  const body = (await res.json()) as { groups?: SourceGroup[] };
  const out: Array<{ path: string; label: string }> = [];
  const seen = new Set<string>();
  for (const g of body.groups ?? []) {
    for (const s of g.sources ?? []) {
      if (!s.path || seen.has(s.path)) continue;
      seen.add(s.path);
      out.push({ path: s.path, label: `${g.label} · ${s.label}` });
    }
  }
  return out;
}

function banner(kind: 'warn' | 'error' | 'info', msg: string): string {
  const cls = kind === 'error' ? 'v-banner-danger' : kind === 'warn' ? 'v-banner-warn' : 'v-banner-info';
  const icon = kind === 'error' ? 'fa-circle-xmark' : kind === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info';
  return `<div class="v-pa-md"><div class="v-banner ${cls}"><span class="v-icon fa-light ${icon}"></span><div>${msg}</div></div></div>`;
}
function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
function escapeAttr(s: string): string { return escapeHtml(s); }
function cssEscape(s: string): string { return s.replace(/["\\]/g, '\\$&'); }
